import { useLocation, useNavigate } from 'react-router-dom';
import type { User } from '../types';
import { hasApiAccess, isAdmin } from '../auth';
import { TLDR_CHANNEL_ID } from '../utils/channelUrl';
import { TldrPromptEditor } from './TldrPromptEditor';
import { BrandMark } from './BrandMark';
import { ThemeToggle } from './ThemeToggle';
import { useAdminMode } from '../providers/UserProvider';
import { useSavedSearchPresence } from '../hooks/useSavedSearchPresence';
import { FOR_YOU_IN_NAVBAR } from '../utils/forYouRelease';

interface ReaderNavbarProps {
  selectedChannelId: string;
  onSelectChannel: (id: string) => void;
  user: User | null;
  onLogout: () => void;
}

export function ReaderNavbar({
  selectedChannelId,
  user,
  onLogout,
}: ReaderNavbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Anonymous visitors see the same feed chrome (issue #825); only the
  // account controls differ -- a sign-in link stands in for the user menu.
  const loggedIn = !!user;
  // Admin chrome is drawn only in admin mode (issue #889). Out of admin mode an
  // admin's view is identical to any other user's -- bar the 'a' hint below and
  // the username, which always leads to /admin so the hub is never unreachable
  // just because this browser has the mode switched off.
  const { adminMode, toggleAdminMode } = useAdminMode();
  const adminUser = isAdmin(user);
  const showAdmin = adminUser && adminMode;
  const showSettings = !adminUser && hasApiAccess(user);
  const searchActive = location.pathname.startsWith('/search/');
  const forYouActive = location.pathname.startsWith('/for-you');
  // Gated on having any feed, not on For You having anything in it: the page's
  // own empty states explain both the no-feeds and the everything-excluded
  // cases, and neither is reachable if the link is hidden.
  // Re-enabled for admins only while scaling and performance are monitored.
  const forYouAllowed = FOR_YOU_IN_NAVBAR || adminUser;
  const hasFeeds = useSavedSearchPresence(forYouAllowed && loggedIn);
  const showForYou = forYouAllowed && hasFeeds;

  return (
    <nav className="reader-navbar">
      <button
        type="button"
        className="reader-navbar-brand"
        onClick={() => navigate('/tldr')}
        aria-label="TLDR Reader home"
      >
        <BrandMark />
        <span className="brand-word">TLDR</span>
      </button>

      <nav className="reader-navbar-links">
        <a href="https://tldr.tech/newsletters">Newsletters</a>
        <a href="https://advertise.tldr.tech/">Advertise</a>
        <a href="https://tldr.tech/blog">Blog</a>
      </nav>

      <TldrPromptEditor canEdit={showAdmin && selectedChannelId === TLDR_CHANNEL_ID} />

      <button
        type="button"
        className={`reader-special-channel-btn${searchActive ? ' active' : ''}`}
        onClick={() => navigate('/search/new')}
      >
        Search
      </button>

      {showForYou && (
        <button
          type="button"
          className={`reader-special-channel-btn${forYouActive ? ' active' : ''}`}
          onClick={() => navigate('/for-you')}
        >
          For You
        </button>
      )}

      <div className="reader-navbar-right">
        <div className="reader-hotkey-hint">
          <kbd>j</kbd>
          <kbd>k</kbd>
          <span>navigate</span>
          <kbd className="hint-gap">o</kbd>
          <span>expand</span>
          {adminUser && (
            <button
              type="button"
              className="reader-hotkey-toggle"
              onClick={toggleAdminMode}
              aria-pressed={adminMode}
              title="Toggle admin mode (a)"
            >
              <kbd className={`hint-gap${adminMode ? ' active' : ''}`}>a</kbd>
              <span>admin mode</span>
            </button>
          )}
        </div>
        <ThemeToggle />
        {!loggedIn ? (
          <button
            type="button"
            className="reader-navbar-user reader-navbar-user-link"
            onClick={() => navigate('/login')}
          >
            Log in
          </button>
        ) : showAdmin || showSettings ? (
          <button
            type="button"
            className="reader-navbar-user reader-navbar-user-link"
            onClick={() => navigate(showAdmin ? '/admin' : '/settings')}
          >
            {user?.username || user?.email || 'User'}
          </button>
        ) : (
          <span className="reader-navbar-user">{user?.username || user?.email || 'User'}</span>
        )}
        {loggedIn && (
          <button type="button" className="reader-navbar-signout" onClick={onLogout}>Sign out</button>
        )}
      </div>
    </nav>
  );
}
